import React, { useMemo, useState } from 'react';
import { Euro, Sparkles, Loader2 } from 'lucide-react';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { storeApi, formatApiError } from '../../lib/api';

/**
 * FinancingToggle: card per attivare/disattivare il finanziamento sul preventivo.
 *
 * - Switch grosso "Attiva finanziamento": ON/OFF
 * - Se ON: l'agente sceglie il tipo tra quelli SUPPORTATI dai prodotti in carrello
 *   (TASSO ZERO 24/48 e/o FIDO in bolletta 48). Mostra rata mensile live.
 * - Se nessun prodotto supporta finanziamento → switch disabilitato.
 *
 * Props:
 *   quote: oggetto preventivo (deve contenere lines + total_gross + selected_financing)
 *   onChange: (updatedQuote) => void  — callback dopo update riuscito
 *   disabled: bool (es. quando preventivo non più in bozza)
 */
export const FinancingToggle = ({ quote, onChange, disabled = false }) => {
  const [saving, setSaving] = useState(false);

  // Calcola opzioni disponibili dagli snapshot prodotti in carrello
  const availableOptions = useMemo(() => {
    const opts = [];
    const seen = new Set();
    (quote?.lines || []).forEach((line) => {
      const snap = line.product_snapshot || {};
      if (snap.tasso_zero_available) {
        const months = snap.financing_months || 24;
        const id = `TASSO_ZERO_${months}`;
        if (!seen.has(id)) {
          seen.add(id);
          opts.push({
            id,
            label: `Tasso Zero ${months} mesi`,
            months,
            description: `Rateizzazione a tasso zero in ${months} rate mensili`,
          });
        }
      }
      if (snap.fido_in_bolletta) {
        const months = snap.fido_months || 48;
        const id = `FIDO_BOLLETTA_${months}`;
        if (!seen.has(id)) {
          seen.add(id);
          opts.push({
            id,
            label: `Fido in bolletta ${months} mesi`,
            months,
            description: `Importo addebitato direttamente in bolletta in ${months} rate`,
          });
        }
      }
    });
    return opts;
  }, [quote]);

  // Stato visibile = il toggle è effettivamente acceso? 
  // Sia il flag DB sia che almeno un'opzione sia ancora supportata.
  const dbActive = Boolean(quote?.selected_financing);
  const noOptions = availableOptions.length === 0;
  const isActive = dbActive && !noOptions;

  const handleToggle = async (next) => {
    if (saving) return;
    setSaving(true);
    try {
      // Se attivato: scegli prima opzione disponibile come default
      const payload = next ? (availableOptions[0]?.id || null) : null;
      const res = await storeApi.updateQuoteFinancing(quote.id, payload);
      onChange?.(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleSelectOption = async (optionId) => {
    if (saving || optionId === quote?.selected_financing) return;
    setSaving(true);
    try {
      const res = await storeApi.updateQuoteFinancing(quote.id, optionId);
      onChange?.(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // Calcolo rata
  const monthsFromId = (id) => {
    if (!id) return null;
    const m = id.match(/_(\d+)$/);
    return m ? parseInt(m[1], 10) : null;
  };
  const months = monthsFromId(quote?.selected_financing);
  const rata = months && quote?.total_gross ? quote.total_gross / months : null;

  return (
    <div
      className={`rounded-xl border p-4 transition-all ${
        isActive
          ? 'bg-gradient-to-br from-amber-50 to-amber-100/60 border-amber-300 shadow-sm'
          : 'bg-slate-50/70 border-slate-200'
      }`}
      data-testid="financing-toggle-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5 flex-1 min-w-0">
          <div className={`mt-0.5 p-1.5 rounded-lg ${isActive ? 'bg-amber-200/70' : 'bg-slate-200/70'}`}>
            <Euro className={`h-4 w-4 ${isActive ? 'text-amber-700' : 'text-slate-500'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-bold ${
              noOptions ? 'text-slate-500' : (isActive ? 'text-amber-900' : 'text-[#0B2A4A]')
            }`}>
              {noOptions
                ? 'Finanziamento non disponibile'
                : (isActive ? 'Finanziamento attivo' : 'Finanziamento non attivo')}
            </p>
            <p className="text-[11px] text-slate-600 mt-0.5 leading-snug">
              {noOptions
                ? 'Nessun prodotto in carrello supporta finanziamento.'
                : isActive
                  ? 'Proposta rateale mostrata al cliente nel PDF.'
                  : 'Attiva per proporre la rateizzazione al cliente.'}
            </p>
          </div>
        </div>
        <Switch
          checked={isActive}
          onCheckedChange={handleToggle}
          disabled={disabled || saving || noOptions}
          className={isActive ? 'data-[state=checked]:bg-amber-500' : ''}
          data-testid="financing-toggle-switch"
        />
      </div>

      {/* Opzioni disponibili (visibili solo se attivo) */}
      {isActive && availableOptions.length > 0 && (
        <div className="mt-3 space-y-2 pt-3 border-t border-amber-200">
          <p className="text-[10px] uppercase tracking-wide text-amber-800 font-semibold flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> Tipo finanziamento
          </p>
          <div className="space-y-1.5">
            {availableOptions.map((opt) => {
              const selected = quote?.selected_financing === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => handleSelectOption(opt.id)}
                  disabled={disabled || saving}
                  className={`w-full text-left p-2.5 rounded-lg border-2 transition-all ${
                    selected
                      ? 'border-amber-500 bg-white shadow-sm'
                      : 'border-amber-200/60 bg-white/60 hover:border-amber-400'
                  } disabled:opacity-60`}
                  data-testid={`financing-option-${opt.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-xs font-bold ${selected ? 'text-amber-900' : 'text-slate-700'}`}>
                      {opt.label}
                    </span>
                    {selected && rata !== null && (
                      <span className="text-sm font-bold text-amber-700 whitespace-nowrap">
                        € {rata.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mese
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">{opt.description}</p>
                </button>
              );
            })}
          </div>
          {saving && (
            <p className="text-[11px] text-amber-700 flex items-center gap-1.5">
              <Loader2 className="h-3 w-3 animate-spin" /> Aggiornamento…
            </p>
          )}
        </div>
      )}
    </div>
  );
};
